<template>
  <div>
    <div
      class="flex w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors"
      :class="isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 bg-white hover:border-indigo-400 hover:bg-gray-50'"
      role="button"
      tabindex="0"
      @click="triggerFileInput"
      @keydown.enter.prevent="triggerFileInput"
      @keydown.space.prevent="triggerFileInput"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <template v-if="modelValue">
        <DocumentTextIcon class="size-10 text-indigo-500" aria-hidden="true" />
        <p class="mt-2 text-sm font-medium text-gray-900">{{ modelValue.name }}</p>
        <p class="mt-1 text-xs text-gray-500">{{ formattedSize }}</p>
        <button
          type="button"
          class="mt-3 inline-flex items-center gap-x-1 text-sm font-semibold text-red-600 hover:text-red-800"
          @click.stop="clearFile"
        >
          <XMarkIcon class="size-4" aria-hidden="true" />
          {{ $gettext("Retirer le fichier") }}
        </button>
      </template>
      <template v-else>
        <DocumentArrowUpIcon class="size-10 text-gray-400" :class="{ 'text-indigo-500': isDragging }" aria-hidden="true" />
        <p class="mt-2 text-sm text-gray-600">
          <span class="font-semibold text-indigo-600">{{ $gettext("Cliquez pour choisir un fichier") }}</span>
          {{ $gettext("ou glissez-déposez le ici") }}
        </p>
        <p class="mt-1 text-xs text-gray-500">
          {{ allowedDocumentTypeStore.allowedExtensions.map((extension) => extension.toUpperCase()).join(", ") }}
        </p>
      </template>
    </div>

    <input
      ref="fileInput"
      type="file"
      class="sr-only"
      :accept="allowedDocumentTypeStore.acceptString"
      @change="onFileInputChange"
    />

    <p v-if="fileValidationError" class="mt-2 text-sm text-red-600">
      {{ fileValidationError }}
    </p>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { DocumentArrowUpIcon, DocumentTextIcon, XMarkIcon } from "@heroicons/vue/24/outline";
import { useGettext } from "vue3-gettext";
import { useAllowedDocumentTypeStore } from "@/stores/allowedDocumentType";

const props = defineProps({
  modelValue: { type: File, default: null },
});

const emit = defineEmits(["update:modelValue"]);
const { $gettext } = useGettext();
const allowedDocumentTypeStore = useAllowedDocumentTypeStore();

const fileInput = ref(null);
const isDragging = ref(false);
const fileValidationError = ref(null);

const formattedSize = computed(() => {
  if (!props.modelValue) return "";
  const megabytes = props.modelValue.size / (1024 * 1024);
  return megabytes < 0.1 ? "< 0,1 Mo" : `${megabytes.toFixed(1)} Mo`;
});

const triggerFileInput = () => {
  fileInput.value?.click();
};

const onDragLeave = (event) => {
  // Ignore dragleave fired when moving over child elements of the zone.
  if (event.currentTarget.contains(event.relatedTarget)) return;
  isDragging.value = false;
};

const onDrop = (event) => {
  isDragging.value = false;
  acceptFile(event.dataTransfer?.files?.[0]);
};

const onFileInputChange = (event) => {
  acceptFile(event.target.files[0]);
  event.target.value = "";
};

const acceptFile = (file) => {
  if (!file) return;

  fileValidationError.value = null;

  const ext = file.name.split(".").pop().toLowerCase();
  if (!allowedDocumentTypeStore.allowedExtensions.includes(ext)) {
    fileValidationError.value = $gettext("Le fichier n'est pas autorisé. Seuls les fichiers de type %{extensions} sont autorisés.", { extensions: allowedDocumentTypeStore.allowedExtensions.join(", ") });
    return;
  }
  const maxSize = allowedDocumentTypeStore.maxSizeFor(ext);
  if (maxSize && file.size > maxSize * 1024 * 1024) {
    fileValidationError.value = $gettext("Le fichier est trop grand. La taille maximale autorisée est de %{maxSize} Mo.", { maxSize: maxSize });
    return;
  }

  emit("update:modelValue", file);
};

const clearFile = () => {
  fileValidationError.value = null;
  emit("update:modelValue", null);
};
</script>
